// ── Gemini Nano Backend ─────────────────────────────────────────────────────
// Chrome's built-in Prompt API (Gemini Nano). Zero setup, smallest model.
// Available in Chrome 138+ (extensions) / Chrome 148+ (web).
//
// Chrome LanguageModel sessions are stateful. The 'system' role is only legal
// as the first message of a *session* (via initialPrompts). Passing {role:
// 'system'} on a later prompt() call throws:
//   "The 'system' role message must be the first message of a session."

import type { AIBackend, AIBackendStatus, AIBackendType } from '../types';

interface LanguageModelAPI {
  availability(opts?: object): Promise<string>;
  create(opts?: object): Promise<LanguageModelSession>;
}

interface LanguageModelSession {
  prompt(message: string | Array<{ role: string; content: string }>, opts?: object): Promise<string>;
  destroy(): void;
}

 
function getLanguageModel(): LanguageModelAPI | null {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (globalThis as any).LanguageModel ?? null;
}

export class GeminiNanoBackend implements AIBackend {
  readonly type: AIBackendType = 'gemini-nano';
  readonly contextWindowTokens = 4096;
  private session: LanguageModelSession | null = null;
  private _systemPrompt: string | null = null;
  private _initialized = false;

  async checkAvailability(): Promise<AIBackendStatus> {
    const lm = getLanguageModel();
    if (!lm) return 'unavailable';
    try {
      const status = await lm.availability();
      if (status === 'available' || status === 'readily') {
        return this.session || this._initialized ? 'ready' : 'downloadable';
      }
      if (status === 'downloading') return 'downloading';
      if (status === 'downloadable') return 'downloadable';
      return 'unavailable';
    } catch {
      return 'unavailable';
    }
  }

  async initialize(onProgress?: (pct: number) => void): Promise<void> {
    await this.createSession('', onProgress);
    this._initialized = true;
  }

  private async createSession(systemPrompt: string, onProgress?: (pct: number) => void): Promise<void> {
    const lm = getLanguageModel();
    if (!lm) throw new Error('Chrome Prompt API not available');

    if (this.session) {
      try { this.session.destroy(); } catch { /* ignore */ }
      this.session = null;
    }

    const opts: Record<string, unknown> = {
      expectedInputs: [{ type: 'text', languages: ['en'] }],
      monitor: (m: EventTarget) => {
        m.addEventListener('downloadprogress', ((e: Event) => {
          const pe = e as Event & { loaded?: number };
          onProgress?.(pe.loaded ?? 0);
        }) as EventListener);
      },
    };

    if (systemPrompt) {
      opts.initialPrompts = [{ role: 'system', content: systemPrompt }];
    }

    try {
      this.session = await lm.create(opts);
    } catch {
      delete opts.expectedInputs;
      this.session = await lm.create(opts);
    }
    this._systemPrompt = systemPrompt || null;
  }

  async prompt(
    systemPrompt: string,
    userMessage: string,
    responseSchema?: object,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _history: Array<{ role: 'user' | 'assistant'; content: string }> = [],
  ): Promise<string> {
    // Recreate when the system prompt changes (e.g. flow ↔ sequence) or after
    // resetConversation(). Do not put 'system' in prompt() — Chrome forbids it
    // after the session has started. History is owned by the session.
    if (!this.session || this._systemPrompt !== systemPrompt) {
      await this.createSession(systemPrompt);
    }
    if (!this.session) throw new Error('Gemini Nano session not created');

    const opts: Record<string, unknown> = {};
    if (responseSchema) {
      opts.responseConstraint = responseSchema;
    }

    return this.session.prompt(userMessage, opts);
  }

  resetConversation(): void {
    if (this.session) {
      try { this.session.destroy(); } catch { /* ignore */ }
      this.session = null;
    }
    this._systemPrompt = null;
  }

  destroy(): void {
    this.resetConversation();
    this._initialized = false;
  }
}
